import { apiService } from "../api.service";
import {
	type CreateWorkflowRuleInfo,
	CreateWorkflowRuleInfoSchema,
	type EditWorkflowRuleVersionInfo,
	EditWorkflowRuleVersionInfoSchema,
	type WorkflowConditionOperatorsByDataType,
	WorkflowConditionOperatorsByDataTypeSchema,
	type WorkflowRuleDisplay,
	WorkflowRuleDisplaySchema,
	type WorkflowRuleScopeInfo,
	type WorkflowRulesParams,
	type WorkflowServiceResult,
	WorkflowServiceResultSchema,
} from "./workflow.types";

const WorkflowRuleDisplayArray = WorkflowRuleDisplaySchema.array();

export class WorkflowApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
		/** Error strings returned by the backend (e.g. from `errors` array in the response body) */
		public readonly serverErrors?: string[],
	) {
		super(message);
		this.name = "WorkflowApiError";
	}
}

/** Extract the `errors` string array from a caught ApiError's response body, if present. */
function extractServerErrors(error: unknown): string[] {
	if (!error || typeof error !== "object") return [];
	const data = (error as Record<string, unknown>).data;
	if (!data || typeof data !== "object") return [];
	const errors = (data as Record<string, unknown>).errors;
	return Array.isArray(errors)
		? errors.filter((e): e is string => typeof e === "string")
		: [];
}

export class WorkflowService {
	/**
	 * Step 1: Create a new workflow rule shell (no trigger/actions yet).
	 * POST /workflows/workflow-rules
	 * Returns ServiceResult; affectedId is the new rule's UUID.
	 */
	async createWorkflowRule(
		data: CreateWorkflowRuleInfo,
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const validated = CreateWorkflowRuleInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.post<unknown>(
				"/workflows/workflow-rules",
				validated,
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			const serverErrors = extractServerErrors(error);
			throw new WorkflowApiError(
				serverErrors.length > 0
					? serverErrors.join(" ")
					: `Failed to create workflow rule: ${JSON.stringify(error instanceof Error ? error.message : error)}`,
				error,
				undefined,
				serverErrors,
			);
		}
	}

	/**
	 * Step 2: Create a new draft version for the rule.
	 * POST /workflows/workflow-rules/{ruleId}/versions
	 * Returns ServiceResult; affectedId is the new version's UUID.
	 */
	async createWorkflowRuleVersion(
		ruleId: string,
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.post<unknown>(
				`/workflows/workflow-rules/${ruleId}/versions`,
				{},
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to create workflow rule version: ${JSON.stringify(error instanceof Error ? error.message : error)}`,
				error,
			);
		}
	}

	/**
	 * Step 3: Save the trigger / condition / actions on the version.
	 * PUT /workflows/workflow-rules/{ruleId}/versions/{versionId}
	 */
	async updateWorkflowRuleVersion(
		ruleId: string,
		versionId: string,
		data: EditWorkflowRuleVersionInfo,
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const validated = EditWorkflowRuleVersionInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.put<unknown>(
				`/workflows/workflow-rules/${ruleId}/versions/${versionId}`,
				validated,
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to update workflow rule version: ${JSON.stringify(error instanceof Error ? error.message : error)}`,
				error,
			);
		}
	}

	/**
	 * GET /workflows/workflow-rules/{start}/{count}
	 * Returns a page of workflow rules matching the given params.
	 * `start` is 1-based (first page = pageIndex * pageSize + 1).
	 */
	async getWorkflowRules(
		start: number,
		count: number,
		params?: WorkflowRulesParams,
		options?: { organizationId?: string },
	): Promise<WorkflowRuleDisplay[]> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.get<unknown>(
				`/workflows/workflow-rules/${start}/${count}`,
				{
					params,
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowRuleDisplayArray.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to fetch workflow rules: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}

	/**
	 * GET /workflows/workflow-rules/count
	 * Returns total rule count matching params.
	 */
	async getWorkflowRulesCount(
		params?: WorkflowRulesParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.get<number>(
				"/workflows/workflow-rules/count",
				{
					params,
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return response;
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to fetch workflow rules count: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}

	/**
	 * PUT /workflows/workflow-rules/{workflowRuleId}/archive
	 */
	async archiveWorkflowRule(
		ruleId: string,
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<unknown>(
				`/workflows/workflow-rules/${ruleId}/archive`,
				{},
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to archive workflow rule: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}

	/**
	 * PUT /workflows/workflow-rules/{ruleId}/versions/{versionId}/activate
	 */
	async activateWorkflowRuleVersion(
		ruleId: string,
		versionId: string,
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<unknown>(
				`/workflows/workflow-rules/${ruleId}/versions/${versionId}/activate`,
				{},
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to activate workflow rule version: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}

	/**
	 * GET /workflows/workflow-rules/{workflowRuleId}
	 * Returns a single workflow rule by id.
	 */
	async getWorkflowRule(
		ruleId: string,
		options?: { organizationId?: string },
	): Promise<WorkflowRuleDisplay> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.get<unknown>(
				`/workflows/workflow-rules/${ruleId}`,
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowRuleDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to fetch workflow rule: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}

	/**
	 * PUT /workflows/workflow-rules/{ruleId}
	 * Updates the name and description of a rule (applies to all statuses).
	 */
	async updateWorkflowRule(
		ruleId: string,
		data: { name: string; description?: string | null },
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<unknown>(
				`/workflows/workflow-rules/${ruleId}`,
				data,
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to update workflow rule: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}

	/**
	 * DELETE /workflows/workflow-rules/{workflowRuleId}
	 * Only draft rules can be deleted.
	 */
	async deleteWorkflowRule(
		ruleId: string,
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.delete<unknown>(
				`/workflows/workflow-rules/${ruleId}`,
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to delete workflow rule: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}
	/**
	 * PUT /workflows/workflow-rules/{ruleId}/scopes
	 * Replaces all scopes for the rule with the provided array.
	 * Pass an empty array to clear all scopes.
	 */
	async updateWorkflowRuleScopes(
		ruleId: string,
		scopes: WorkflowRuleScopeInfo[],
		options?: { organizationId?: string },
	): Promise<WorkflowServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<unknown>(
				`/workflows/workflow-rules/${ruleId}/scopes`,
				scopes,
				{
					headers:
						organizationId !== undefined
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return WorkflowServiceResultSchema.parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to update workflow rule scopes: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}

	/** GET /workflows/condition-operators-by-data-type */
	async getConditionOperatorsByDataType(): Promise<
		WorkflowConditionOperatorsByDataType[]
	> {
		try {
			const response = await apiService.get<
				WorkflowConditionOperatorsByDataType[]
			>("/workflows/condition-operators-by-data-type");
			return WorkflowConditionOperatorsByDataTypeSchema.array().parse(response);
		} catch (error) {
			if (error instanceof WorkflowApiError) throw error;
			throw new WorkflowApiError(
				`Failed to fetch condition operators: ${error instanceof Error ? error.message : String(error)}`,
				error,
			);
		}
	}
}

export const workflowService = new WorkflowService();
